import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import {
  FileText,
  Sparkles,
  ArrowLeft,
  Copy,
  Download,
  Loader2,
  Lock,
  Trash2,
  Save as SaveIcon,
  Plus,
  X,
} from "lucide-react";
import { copyToClipboard } from "../lib/clipboard";

// Small helper: render a very simple markdown-ish preview inline (headings +
// bullets). We deliberately don't ship a full markdown lib for a one-page tool.
function MarkdownPreview({ text }) {
  const lines = (text || "").split("\n");
  const els = [];
  let listBuffer = [];
  const flushList = () => {
    if (listBuffer.length) {
      els.push(<ul key={`ul-${els.length}`} className="list-disc pl-6 mb-4 space-y-1 text-[#111]">{listBuffer.map((li, i) => <li key={i}>{li}</li>)}</ul>);
      listBuffer = [];
    }
  };
  for (const raw of lines) {
    const l = raw.trimEnd();
    if (!l.trim()) { flushList(); els.push(<div key={`sp-${els.length}`} className="h-2" />); continue; }
    const boldWholeMatch = /^\*\*(.+)\*\*(?::)?\s*$/.exec(l);
    if (boldWholeMatch) { flushList(); els.push(<h3 key={`h-${els.length}`} className="font-display font-bold text-lg mt-4">{boldWholeMatch[1]}</h3>); continue; }
    if (/^#{1,6}\s/.test(l)) { flushList(); const t = l.replace(/^#{1,6}\s/, ""); els.push(<h3 key={`h-${els.length}`} className="font-display font-bold text-xl mt-6 mb-1">{t}</h3>); continue; }
    if (/^\d+\.\s/.test(l)) { flushList(); const t = l.replace(/^\d+\.\s/, ""); els.push(<h4 key={`h-${els.length}`} className="font-display font-semibold mt-5 mb-1 text-[#111]">{t}</h4>); continue; }
    if (l.startsWith("- ") || l.startsWith("* ")) { listBuffer.push(l.slice(2)); continue; }
    flushList();
    els.push(<p key={`p-${els.length}`} className="mb-2 leading-relaxed text-[#333]">{l}</p>);
  }
  flushList();
  return <div className="cal-prose">{els}</div>;
}

// Trigger a browser download of the .docx for a given document id. We use a
// hidden anchor to a same-origin URL so the browser handles the file save
// dialog + filename from Content-Disposition natively — no blob juggling.
async function downloadDocx(docId, fallbackTitle) {
  try {
    const r = await api.get(`/pd-generator/documents/${docId}/docx`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    const safe = (fallbackTitle || "position-description").replace(/[^a-z0-9-_]+/gi, "-").toLowerCase() || "position-description";
    a.download = `${safe}.docx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    return true;
  } catch (err) {
    toast.error(apiError(err, "Failed to download .docx"));
    return false;
  }
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default function PDGenerator() {
  const nav = useNavigate();
  const [jobTitle, setJobTitle] = useState("");
  const [department, setDepartment] = useState("");
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(1); // 1 = intake, 2 = questions, 3 = result
  const [loadingQs, setLoadingQs] = useState(false);
  const [loadingPd, setLoadingPd] = useState(false);
  const [markdown, setMarkdown] = useState("");
  const [currentDocId, setCurrentDocId] = useState(null); // id of the doc backing the current Step-3 view
  const [quota, setQuota] = useState(null);
  const [quotaLoading, setQuotaLoading] = useState(true);

  // Saved document library
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [selected, setSelected] = useState(null); // full doc: {id, job_title, department, markdown, ...}
  const [editing, setEditing] = useState(false);
  const [editMd, setEditMd] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editDept, setEditDept] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const refreshQuota = useCallback(async () => {
    try {
      const r = await api.get("/pd-generator/quota");
      setQuota(r.data);
    } catch (err) {
      console.warn("pd quota fetch failed", err);
    } finally {
      setQuotaLoading(false);
    }
  }, []);

  const refreshDocs = useCallback(async () => {
    try {
      const r = await api.get("/pd-generator/documents");
      setDocs(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      console.warn("pd documents fetch failed", err);
    } finally {
      setDocsLoading(false);
    }
  }, []);

  useEffect(() => { refreshQuota(); refreshDocs(); }, [refreshQuota, refreshDocs]);

  const locked = !!quota?.locked;

  const askQuestions = useCallback(async (e) => {
    e?.preventDefault?.();
    if (jobTitle.trim().length < 2 || department.trim().length < 2) {
      toast.error("Both Job Title and Department need at least 2 characters");
      return;
    }
    setLoadingQs(true);
    try {
      const r = await api.post("/pd-generator/questions", { job_title: jobTitle.trim(), department: department.trim() });
      const qs = r.data.questions || [];
      if (!qs.length) throw new Error("no questions returned");
      setQuestions(qs);
      setAnswers({});
      setStep(2);
    } catch (err) { toast.error(apiError(err, "Failed to generate questions")); }
    finally { setLoadingQs(false); }
  }, [jobTitle, department]);

  const generatePD = useCallback(async () => {
    const nonEmpty = Object.values(answers).filter((v) => (v || "").trim().length > 0).length;
    if (nonEmpty < Math.min(2, questions.length)) {
      toast.error("Please answer at least 2 questions with a few keywords each");
      return;
    }
    setLoadingPd(true);
    try {
      const r = await api.post("/pd-generator/generate", {
        job_title: jobTitle.trim(),
        department: department.trim(),
        answers,
        questions,
      });
      setMarkdown(r.data.markdown || "");
      setCurrentDocId(r.data.id || null);
      if (r.data.quota) setQuota(r.data.quota);
      setStep(3);
      refreshDocs();
    } catch (err) {
      toast.error(apiError(err, "Failed to generate PD"));
      refreshQuota();
    }
    finally { setLoadingPd(false); }
  }, [answers, jobTitle, department, questions, refreshQuota, refreshDocs]);

  const copyMd = async () => {
    const ok = await copyToClipboard(markdown);
    if (ok) toast.success("Copied markdown to clipboard");
    else toast.error("Copy blocked by browser — use the Download button instead");
  };

  const startNew = () => {
    setStep(1);
    setJobTitle(""); setDepartment(""); setQuestions([]); setAnswers({}); setMarkdown("");
    setCurrentDocId(null);
    setSelected(null);
    setEditing(false);
  };

  // ---- Library actions -----------------------------------------------------
  const openDoc = useCallback(async (docId) => {
    try {
      const r = await api.get(`/pd-generator/documents/${docId}`);
      setSelected(r.data);
      setEditMd(r.data.markdown || "");
      setEditTitle(r.data.job_title || "");
      setEditDept(r.data.department || "");
      setEditing(false);
    } catch (err) {
      toast.error(apiError(err, "Failed to open PD"));
    }
  }, []);

  const saveDoc = useCallback(async () => {
    if (!selected) return;
    if (editTitle.trim().length < 1 || editDept.trim().length < 1) {
      toast.error("Job title and department cannot be empty");
      return;
    }
    setSaving(true);
    try {
      const r = await api.patch(`/pd-generator/documents/${selected.id}`, {
        markdown: editMd,
        job_title: editTitle.trim(),
        department: editDept.trim(),
      });
      setSelected(r.data);
      setEditing(false);
      // If the Step-3 view is showing this same doc, update its markdown too
      if (currentDocId === selected.id) setMarkdown(r.data.markdown || "");
      toast.success("PD saved");
      refreshDocs();
    } catch (err) {
      toast.error(apiError(err, "Failed to save PD"));
    } finally {
      setSaving(false);
    }
  }, [selected, editMd, editTitle, editDept, currentDocId, refreshDocs]);

  const deleteDoc = useCallback(async () => {
    if (!selected) return;
    if (!window.confirm(`Delete "${selected.job_title}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/pd-generator/documents/${selected.id}`);
      toast.success("PD deleted");
      if (currentDocId === selected.id) {
        setCurrentDocId(null);
        // Leave the generated markdown on screen but drop the reference; the
        // user can still copy/download the raw text if they want.
      }
      setSelected(null);
      setEditing(false);
      refreshDocs();
    } catch (err) {
      toast.error(apiError(err, "Failed to delete PD"));
    } finally {
      setDeleting(false);
    }
  }, [selected, currentDocId, refreshDocs]);

  const downloadSelectedDocx = useCallback(async () => {
    if (!selected) return;
    await downloadDocx(selected.id, selected.job_title);
  }, [selected]);

  const downloadCurrentDocx = useCallback(async () => {
    if (currentDocId) {
      await downloadDocx(currentDocId, jobTitle);
    } else {
      // Fallback for the unusual case where persistence failed: use a client-
      // side .md so the user isn't stranded. The DOCX endpoint requires a
      // persisted document.
      toast.error("This PD wasn't saved to the library — please generate again.");
    }
  }, [currentDocId, jobTitle]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="pd-generator-page">
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-3">
        <div>
          <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#FF4F00]">[ PD GENERATOR · AI ]</div>
          <h1 className="font-display font-bold text-4xl tracking-tight mt-1 flex items-center gap-3">
            <FileText size={32} className="text-[#FF4F00]" /> Draft a Position Description
          </h1>
        </div>
        <button onClick={() => nav("/dashboard")} className="cal-btn cal-btn-ghost" data-testid="pd-back-dashboard">
          <ArrowLeft size={14} className="inline mr-1" /> Back to dashboard
        </button>
      </div>
      <p className="text-[#666] mb-6 max-w-2xl">
        Two AI steps. First, tell us the role — we generate 4–5 targeted discovery questions so the
        final document isn&apos;t generic. Then we compose the full PD in seconds.
      </p>

      {/* Quota banner */}
      {!quotaLoading && quota && (
        <div
          className={`cal-card p-4 mb-8 flex items-start gap-4 flex-wrap ${locked ? "border-2 border-[#B00020] bg-[#FFF5F6]" : "border-[#111]"}`}
          data-testid="pd-quota-banner"
        >
          <div className="flex-1 min-w-[220px]">
            <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-1">
              [ PD GENERATOR QUOTA · {quota.plan_name || quota.plan_slug} ]
            </div>
            {quota.lifetime ? (
              locked ? (
                <div className="font-display font-semibold text-[#B00020]" data-testid="pd-quota-summary">
                  You&apos;ve used your <b>1 free</b> PD generation. Upgrade to a paid plan to generate more.
                </div>
              ) : (
                <div className="font-display font-semibold" data-testid="pd-quota-summary">
                  <b>1</b> free PD generation available on the Free plan. Upgrade to any paid plan for
                  larger quotas that reset annually.
                </div>
              )
            ) : (
              <div className="font-display font-semibold" data-testid="pd-quota-summary">
                <b>{quota.remaining}</b> of <b>{quota.limit}</b> PD generations remaining
                {quota.resets_at ? (
                  <> · resets on <span className="text-[#FF4F00]">{formatDate(quota.resets_at)}</span></>
                ) : null}
              </div>
            )}
          </div>
          {locked && (
            <Link to="/admin/org" className="cal-btn" data-testid="pd-quota-upgrade-cta">
              <Lock size={14} className="inline mr-1" /> Upgrade plan →
            </Link>
          )}
        </div>
      )}

      {/* Main split: generation flow (left/main) + saved library (right pane).
          On mobile the library stacks under the main flow. */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 items-start">
        <div>
          {/* Stepper */}
          <div className="flex items-center gap-2 mb-8 font-mono-cal text-[10px] uppercase tracking-widest">
            {["Role", "Context", "Result"].map((label, i) => {
              const active = step === i + 1;
              const done = step > i + 1;
              return (
                <React.Fragment key={label}>
                  <div className={`px-3 py-1 border ${active ? "bg-[#111] text-white border-[#111]" : done ? "text-[#FF4F00] border-[#FF4F00]" : "text-[#999] border-[#DDD]"}`}>
                    {i + 1}. {label}
                  </div>
                  {i < 2 && <div className={`h-px flex-1 ${done ? "bg-[#FF4F00]" : "bg-[#DDD]"}`} />}
                </React.Fragment>
              );
            })}
          </div>

          {/* STEP 1 — role intake */}
          {step === 1 && (
            <form onSubmit={askQuestions} className="cal-card p-8 space-y-5" data-testid="pd-step-1">
              <div>
                <label className="cal-label">Job title *</label>
                <input className="cal-input" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} required minLength={2} maxLength={140} placeholder="e.g. Senior DevOps Engineer" data-testid="pd-job-title" />
              </div>
              <div>
                <label className="cal-label">Department *</label>
                <input className="cal-input" value={department} onChange={(e) => setDepartment(e.target.value)} required minLength={2} maxLength={140} placeholder="e.g. IT Infrastructure & Cloud Operations" data-testid="pd-department" />
              </div>
              <button type="submit" disabled={loadingQs || locked} className="cal-btn w-full" data-testid="pd-ask-questions">
                {locked ? <><Lock size={14} className="inline mr-1" /> Quota exhausted — upgrade to continue</>
                : loadingQs ? <><Loader2 size={14} className="inline mr-1 animate-spin" /> Generating targeted questions…</>
                : <><Sparkles size={14} className="inline mr-1" /> Generate discovery questions →</>}
              </button>
              <p className="text-xs font-mono-cal text-[#666]">
                Uses ~1s of Anthropic Claude time. You can regenerate questions if the first pass misses the mark.
              </p>
            </form>
          )}

          {/* STEP 2 — dynamic questions */}
          {step === 2 && (
            <div className="space-y-6" data-testid="pd-step-2">
              <div className="cal-card p-4 bg-[#F4F4F0]">
                <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666]">[ ROLE ]</div>
                <div className="font-display font-bold text-lg">{jobTitle} · <span className="text-[#666] font-normal">{department}</span></div>
              </div>
              <div className="space-y-4">
                {questions.map((q, idx) => (
                  <div key={q.id} className="cal-card p-5" data-testid={`pd-question-${q.id}`}>
                    <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] mb-1">Q{idx + 1}</div>
                    <label className="block font-display font-semibold text-base mb-2">{q.label}</label>
                    <textarea
                      rows={3}
                      className="w-full p-3 border border-[#111] focus:ring-2 focus:ring-[#FF4F00] outline-none resize-y text-sm"
                      placeholder={q.placeholder || "Your answer…"}
                      value={answers[q.id] || ""}
                      onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                      data-testid={`pd-answer-${q.id}`}
                    />
                  </div>
                ))}
              </div>
              <div className="flex gap-3 items-center flex-wrap">
                <button onClick={() => { setStep(1); setQuestions([]); }} className="cal-btn cal-btn-ghost" data-testid="pd-back-step-1">← Change role</button>
                <button onClick={askQuestions} disabled={loadingQs} className="cal-btn cal-btn-ghost" data-testid="pd-regen-questions">
                  {loadingQs ? "Regenerating…" : "↻ Regenerate questions"}
                </button>
                <div className="flex-1" />
                <button onClick={generatePD} disabled={loadingPd || locked} className="cal-btn" data-testid="pd-generate">
                  {locked ? <><Lock size={14} className="inline mr-1" /> Quota exhausted</>
                  : loadingPd ? <><Loader2 size={14} className="inline mr-1 animate-spin" /> Writing your PD…</>
                  : <><Sparkles size={14} className="inline mr-1" /> Generate PD →</>}
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 — result */}
          {step === 3 && (
            <div className="space-y-4" data-testid="pd-step-3">
              <div className="cal-card p-4 bg-[#F4F4F0] flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666]">[ COMPLETE · SAVED TO LIBRARY ]</div>
                  <div className="font-display font-bold text-lg">{jobTitle}</div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={copyMd} className="cal-btn cal-btn-ghost" data-testid="pd-copy">
                    <Copy size={14} className="inline mr-1" /> Copy markdown
                  </button>
                  <button onClick={downloadCurrentDocx} className="cal-btn cal-btn-ghost" data-testid="pd-download">
                    <Download size={14} className="inline mr-1" /> Download .docx
                  </button>
                  <button onClick={startNew} className="cal-btn" data-testid="pd-restart">
                    <Plus size={14} className="inline mr-1" /> New PD
                  </button>
                </div>
              </div>
              <div className="cal-card p-8" data-testid="pd-result">
                <MarkdownPreview text={markdown} />
              </div>
            </div>
          )}
        </div>

        {/* ===================== RIGHT PANE — SAVED PDS ===================== */}
        <aside className="cal-card p-5 lg:sticky lg:top-6 max-h-[calc(100vh-3rem)] overflow-hidden flex flex-col" data-testid="pd-library">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00]">[ SAVED PDS ]</div>
              <div className="font-display font-bold text-lg">Your library</div>
            </div>
            <div className="font-mono-cal text-[10px] text-[#666]" data-testid="pd-library-count">{docs.length}</div>
          </div>
          <div className="border-t border-[#EEE] -mx-5 mb-3" />
          <div className="flex-1 overflow-y-auto -mx-5 px-5" data-testid="pd-library-list">
            {docsLoading ? (
              <div className="text-sm text-[#666] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
            ) : docs.length === 0 ? (
              <div className="text-sm text-[#666]" data-testid="pd-library-empty">
                No saved PDs yet. Generate one above and it&apos;ll appear here.
              </div>
            ) : (
              <ul className="space-y-2">
                {docs.map((d) => {
                  const isActive = selected?.id === d.id;
                  return (
                    <li key={d.id}>
                      <button
                        onClick={() => openDoc(d.id)}
                        className={`w-full text-left p-3 border transition-colors ${isActive ? "bg-[#111] text-white border-[#111]" : "border-[#DDD] hover:border-[#111] bg-white"}`}
                        data-testid={`pd-library-item-${d.id}`}
                      >
                        <div className="font-display font-semibold text-sm truncate">{d.job_title}</div>
                        <div className={`text-xs truncate ${isActive ? "text-[#DDD]" : "text-[#666]"}`}>{d.department}</div>
                        <div className={`font-mono-cal text-[10px] mt-1 ${isActive ? "text-[#BBB]" : "text-[#999]"}`}>
                          {formatDate(d.updated_at || d.created_at)}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>
      </div>

      {/* ===================== VIEWER / EDITOR MODAL ===================== */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-start md:items-center justify-center p-4 overflow-y-auto"
          data-testid="pd-viewer-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setSelected(null); }}
        >
          <div className="bg-white w-full max-w-4xl my-8 border-2 border-[#111] shadow-xl">
            <div className="p-5 border-b border-[#EEE] flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00]">[ SAVED PD ]</div>
                {editing ? (
                  <div className="space-y-2 mt-1">
                    <input
                      className="cal-input"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      maxLength={200}
                      placeholder="Job title"
                      data-testid="pd-editor-title"
                    />
                    <input
                      className="cal-input"
                      value={editDept}
                      onChange={(e) => setEditDept(e.target.value)}
                      maxLength={200}
                      placeholder="Department"
                      data-testid="pd-editor-department"
                    />
                  </div>
                ) : (
                  <>
                    <div className="font-display font-bold text-xl mt-1">{selected.job_title}</div>
                    <div className="text-sm text-[#666]">{selected.department}</div>
                    <div className="font-mono-cal text-[10px] text-[#999] mt-1">
                      Updated {formatDate(selected.updated_at || selected.created_at)}
                    </div>
                  </>
                )}
              </div>
              <button
                onClick={() => setSelected(null)}
                className="cal-btn cal-btn-ghost"
                data-testid="pd-viewer-close"
                aria-label="Close"
              >
                <X size={14} />
              </button>
            </div>

            <div className="p-5 max-h-[60vh] overflow-y-auto" data-testid="pd-viewer-body">
              {editing ? (
                <textarea
                  className="w-full min-h-[50vh] p-3 border border-[#111] focus:ring-2 focus:ring-[#FF4F00] outline-none font-mono text-sm"
                  value={editMd}
                  onChange={(e) => setEditMd(e.target.value)}
                  data-testid="pd-editor-markdown"
                />
              ) : (
                <MarkdownPreview text={selected.markdown} />
              )}
            </div>

            <div className="p-5 border-t border-[#EEE] flex flex-wrap gap-2 justify-end">
              {editing ? (
                <>
                  <button
                    onClick={() => { setEditing(false); setEditMd(selected.markdown || ""); setEditTitle(selected.job_title || ""); setEditDept(selected.department || ""); }}
                    className="cal-btn cal-btn-ghost"
                    disabled={saving}
                    data-testid="pd-editor-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveDoc}
                    className="cal-btn"
                    disabled={saving}
                    data-testid="pd-editor-save"
                  >
                    {saving ? <><Loader2 size={14} className="inline mr-1 animate-spin" /> Saving…</> : <><SaveIcon size={14} className="inline mr-1" /> Save</>}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={deleteDoc}
                    className="cal-btn cal-btn-ghost text-[#B00020]"
                    disabled={deleting}
                    data-testid="pd-viewer-delete"
                  >
                    {deleting ? <><Loader2 size={14} className="inline mr-1 animate-spin" /> Deleting…</> : <><Trash2 size={14} className="inline mr-1" /> Delete</>}
                  </button>
                  <button
                    onClick={downloadSelectedDocx}
                    className="cal-btn cal-btn-ghost"
                    data-testid="pd-viewer-download"
                  >
                    <Download size={14} className="inline mr-1" /> Download .docx
                  </button>
                  <button
                    onClick={() => setEditing(true)}
                    className="cal-btn"
                    data-testid="pd-viewer-edit"
                  >
                    <SaveIcon size={14} className="inline mr-1" /> Edit
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-16 text-xs font-mono-cal text-[#666]">
        <Link to="/dashboard" className="hover:text-[#FF4F00]">← Back to dashboard</Link>
      </div>
    </div>
  );
}
